import React, { useEffect, useRef, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";

// Version actuelle du code frontend chargé par le navigateur
const CURRENT_VERSION = "1.0.0";
// Intervalle de vérification : 2 minutes (120 000 ms)
const CHECK_INTERVAL = 120000;
const REQUEST_TIMEOUT = 5000;

export default function VersionChecker() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const inFlightRef = useRef(false);
  const activeControllerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const checkVersion = async () => {
      if (inFlightRef.current) {
        return;
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      inFlightRef.current = true;
      activeControllerRef.current = controller;

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const response = await fetch(`${backendUrl}/api/version`, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          if (isMounted && data && data.version && data.version !== CURRENT_VERSION) {
            setHasUpdate(true);
          }
        }
      } catch {
        // Ignorer silencieusement les erreurs réseau temporaires quand le backend
        // est indisponible pour éviter le bruit console et les faux signaux.
      } finally {
        window.clearTimeout(timeoutId);
        if (activeControllerRef.current === controller) {
          activeControllerRef.current = null;
        }
        inFlightRef.current = false;
      }
    };

    // Vérifier immédiatement au chargement
    checkVersion();

    // Configurer l'intervalle périodique
    const timer = setInterval(checkVersion, CHECK_INTERVAL);

    return () => {
      isMounted = false;
      clearInterval(timer);
      activeControllerRef.current?.abort();
    };
  }, []);

  const handleUpdate = () => {
    // Rechargement forcé de la page pour vider le cache et obtenir la nouvelle version
    window.location.reload();
  };

  if (!hasUpdate) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-8 duration-500">
      <div className="relative overflow-hidden rounded-2xl border border-white/20 bg-stone-900/90 p-5 text-white shadow-2xl backdrop-blur-md max-w-sm">
        {/* Lueur d'ambiance en arrière-plan */}
        <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-amber-500/10 blur-xl"></div>
        <div className="absolute -left-10 -bottom-10 h-28 w-28 rounded-full bg-orange-500/10 blur-xl"></div>

        <div className="relative flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-amber-500 to-orange-400 text-stone-900 shadow-md">
            <Sparkles className="h-5 w-5 animate-pulse" />
          </div>

          <div className="flex-1 space-y-3">
            <div className="space-y-1">
              <h4 className="font-serif text-base font-semibold tracking-wide text-amber-100">
                Mise à jour disponible !
              </h4>
              <p className="text-xs text-stone-300 leading-relaxed">
                Une nouvelle version de votre application est disponible. Rechargez pour en profiter immédiatement.
              </p>
            </div>

            <button
              onClick={handleUpdate}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 py-2.5 px-4 text-sm font-semibold text-stone-950 shadow-lg shadow-orange-500/25 transition-all hover:scale-[1.02] hover:shadow-orange-500/35 active:scale-[0.98]"
            >
              <RefreshCw className="h-4 w-4" />
              Mettre à jour
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
